import { RotateCcw, Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { receivedDocumentsApi, routingDocumentTypesApi } from '../routingApi';
import { RECEIVING_PATH, ROUTING_SETTINGS_PATH, routeDisabledReason } from '../routingRules';
import {
  RECEIVING_AUDIT_TYPES,
  type ReceivedDocumentDetail,
  type ReceivedDocumentListItem,
} from '../routingTypes';
import { ReceivedDocumentModal } from '../components/ReceivedDocumentModal';
import { RouteDocumentModal } from '../components/RouteDocumentModal';
import { useRoutingDesk } from '../useRoutingDesk';

/**
 * Receiving — every hard copy that came in, where it is now, and how it stands. A file sent
 * between units is not here: it is a dispatch, and it lands in the recipient unit's Inbox.
 *
 * **Newest first** — a receiving desk reads today's mail first. The routing state is the STAGE
 * column (`Received` · `In transit` · `Dispatched`), drawn beside the live-or-deleted status pill;
 * it is also the first facet, so "what is in transit" is one press in the rail.
 *
 * **No Clear data** — emptying the log of what came in is not the tidy-up that key exists for, and
 * the module declares none.
 */
export function ReceivedDocumentsIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ReceivedDocumentDetail | null>(null);
  const [routing, setRouting] = useState<ReceivedDocumentDetail | null>(null);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; row: ReceivedDocumentListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    THE ONE REQUIRED CATALOG — a document cannot be encoded without a type. Seeded MET, so a feed
    that has not answered (or failed, or that this reader cannot call) never puts a banner over a
    working module.
  */
  const [hasType, setHasType] = useState(true);

  // Where this reader sits for routing — the row menu mirrors the server's refusal, and the banner
  // says up front when the account can route nothing at all.
  const { desk, prerequisite: deskPrerequisite } = useRoutingDesk(receivedDocumentsApi.desk);

  useEffect(() => {
    const controller = new AbortController();

    routingDocumentTypesApi
      .options(controller.signal)
      .then((options) => {
        if (!controller.signal.aborted) setHasType(options.length > 0);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [reloadKey]);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      receivedDocumentsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          routing: query.facets.routing?.join(','),
          documentType: query.facets.documentType?.join(','),
          sender: query.facets.sender?.join(','),
          holder: query.facets.holder?.join(','),
        },
        signal,
      ),
    [],
  );

  /** Edit and Route need the DETAIL — the sender's id, the reference, whether the unit is locked. */
  const openWithDetail = useCallback(
    (row: ReceivedDocumentListItem, then: (detail: ReceivedDocumentDetail) => void) => {
      receivedDocumentsApi
        .get(row.id)
        .then(then)
        .catch((caught: unknown) =>
          toast.failure(caught instanceof ApiError ? caught.message : `Could not open ${row.trackingNumber}.`),
        );
    },
    [toast],
  );

  const rowActions = useCallback(
    (row: ReceivedDocumentListItem): MenuItem[] => [
      {
        key: `route-${row.id}`,
        // Receiving's word for routing — product call, 10 Oct 2026.
        label: 'Dispatch',
        icon: <Send className="h-4 w-4" aria-hidden="true" />,
        permission: 'document-receiving.route',
        disabledReason: routeDisabledReason(row, desk),
        onClick: () => openWithDetail(row, setRouting),
      },
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'document-receiving.delete',
        disabledReason: row.isActive ? 'This document is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [openWithDetail, desk],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await receivedDocumentsApi.remove(confirming.row.id);
      else await receivedDocumentsApi.restore(confirming.row.id);

      toast.success(`${confirming.kind === 'delete' ? 'Deleted' : 'Restored'} ${confirming.row.trackingNumber}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<ReceivedDocumentListItem>
        module="document-receiving"
        moduleNoun="received documents"
        breadcrumb={['Document Management', 'Routing', 'Receiving']}
        columns={COLUMNS}
        facets={[
          { key: 'routing', label: 'Routing' },
          { key: 'documentType', label: 'Document Type' },
          { key: 'sender', label: 'From' },
          { key: 'holder', label: 'With' },
          { key: 'status', label: 'Status' },
        ]}
        auditEntityTypes={RECEIVING_AUDIT_TYPES}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by subject, tracking no., sender, sender's reference, type or unit holding it"
        emptyMessage="Nothing received yet. Encode each hard copy as it arrives at the desk."
        prerequisites={[
          {
            label: 'document types',
            met: hasType,
            to: `${ROUTING_SETTINGS_PATH}/document-types`,
            permission: 'routing-document-types.read',
          },
          deskPrerequisite,
        ]}
        onCreate={() => setCreating(true)}
        createPermission="document-receiving.create"
        onRowOpen={(row) => navigate(`${RECEIVING_PATH}/${row.id}`)}
        openPermission="document-receiving.read"
        rowActions={rowActions}
        onEdit={(row) => openWithDetail(row, setEditing)}
        editPermission="document-receiving.update"
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="document-receiving.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) => (row.isActive ? null : 'This document is already deleted.')}
        otherInfo={(row) => row}
      />

      {creating || editing !== null ? (
        <ReceivedDocumentModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(saved) => {
            const created = editing === null;

            setCreating(false);
            setEditing(null);
            refresh();

            // A new document opens on its own profile — where it is routed on.
            if (created) navigate(`${RECEIVING_PATH}/${saved.id}`);
          }}
        />
      ) : null}

      {routing ? (
        <RouteDocumentModal
          open
          api={receivedDocumentsApi}
          document={routing}
          verb="Dispatch"
          onClose={() => setRouting(null)}
          onSaved={() => {
            setRouting(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete received document' : 'Restore received document'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`received-document-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'delete'
                  ? `${confirming.row.trackingNumber} — ${confirming.row.subject} leaves the register. Its route is kept, and a deleted document can be restored from this list.`
                  : `${confirming.row.trackingNumber} — ${confirming.row.subject} returns to the register, where it was.`}
              </p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<ReceivedDocumentListItem>[] = [
  {
    /*
      THE ANCHOR joins the tracking number and the subject on ONE line — the anchor exception. The
      subject is what a reader recognises; the number is what they quote.
    */
    key: 'subject',
    header: 'Document',
    width: 340,
    flex: true,
    sortable: true,
    render: (row) => <TextCell code={row.trackingNumber} value={row.subject} clamp />,
  },
  {
    key: 'documentTypeName',
    header: 'Type',
    width: 150,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.documentTypeName} />,
  },
  {
    // A unit of this organization, or the outside organization as typed — one sender, one value.
    key: 'sender',
    header: 'From',
    width: 220,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.senderOrgUnitName ?? row.senderOffice} clamp />,
  },
  {
    key: 'receivedAt',
    header: 'Received',
    width: 170,
    sortable: true,
    render: (row) => <DateCell value={row.receivedAt} />,
  },
  {
    /*
      A SET since 10 Oct 2026 — one value whose value is several units, joined on one line. Not
      sortable (a set has no order a header could promise); the With facet narrows by it.
    */
    key: 'holders',
    header: 'With',
    width: 220,
    summary: true,
    render: (row) => <TextCell value={row.holders.length > 0 ? row.holders.map((h) => h.name).join(', ') : null} clamp />,
  },
  {
    key: 'routing',
    header: 'Routing',
    width: 130,
    stage: true,
    render: (row) => <StatusText tone="inactive">{row.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
